import React, { useState, useEffect, useRef } from 'react';
import './App.css';

function App() {
  // State variables
  const [breakLength, setBreakLength] = useState(5);
  const [sessionLength, setSessionLength] = useState(25);
  const [timeLeft, setTimeLeft] = useState(25 * 60); // in seconds
  const [isRunning, setIsRunning] = useState(false);
  const [timerType, setTimerType] = useState('Session');
  
  // Refs
  const audioBeep = useRef(null);
  const timerRef = useRef(null);

  // Format time to mm:ss
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle break decrement
  const decrementBreak = () => {
    if (breakLength > 1 && breakLength <= 60) {
      setBreakLength(breakLength - 1);
    }
  };

  // Handle break increment
  const incrementBreak = () => {
    if (breakLength >= 1 && breakLength < 60) {
      setBreakLength(breakLength + 1);
    }
  };

  // Handle session decrement
  const decrementSession = () => {
    if (sessionLength > 1 && sessionLength <= 60) {
      setSessionLength(sessionLength - 1);
      if (!isRunning && timerType === 'Session') {
        setTimeLeft((sessionLength - 1) * 60);
      }
    }
  };

  // Handle session increment
  const incrementSession = () => {
    if (sessionLength >= 1 && sessionLength < 60) {
      setSessionLength(sessionLength + 1);
      if (!isRunning && timerType === 'Session') {
        setTimeLeft((sessionLength + 1) * 60);
      }
    }
  };

  // Reset everything
  const reset = () => {
    setIsRunning(false);
    setBreakLength(5);
    setSessionLength(25);
    setTimeLeft(25 * 60);
    setTimerType('Session');
    
    // Stop and reset audio
    if (audioBeep.current) {
      audioBeep.current.pause();
      audioBeep.current.currentTime = 0;
    }
    
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  // Start/stop timer
  const toggleTimer = () => {
    setIsRunning(!isRunning);
  };

  // Timer logic
  useEffect(() => {
    if (isRunning) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime === 0) {
            // Time's up - play sound and switch timer type
            if (audioBeep.current) {
              audioBeep.current.play();
            }
            
            if (timerType === 'Session') {
              setTimerType('Break');
              return breakLength * 60;
            } else {
              setTimerType('Session');
              return sessionLength * 60;
            }
          }
          return prevTime - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRunning, breakLength, sessionLength, timerType]);

  // Update timeLeft when sessionLength changes and timer is not running
  useEffect(() => {
    if (!isRunning && timerType === 'Session') {
      setTimeLeft(sessionLength * 60);
    }
  }, [sessionLength, isRunning, timerType]);

  return (
    <div className="pomodoro-clock">
      <h1>25 + 5 Clock</h1>
      
      <div className="controls-container">
        {/* Break Length Control */}
        <div className="length-control">
          <div id="break-label">Break Length</div>
          <div className="control-buttons">
            <button 
              id="break-decrement" 
              className="btn-level" 
              onClick={decrementBreak}
            >
              <i className="fa fa-arrow-down fa-2x"></i>
            </button>
            <div id="break-length" className="btn-level">
              {breakLength}
            </div>
            <button 
              id="break-increment" 
              className="btn-level" 
              onClick={incrementBreak}
            >
              <i className="fa fa-arrow-up fa-2x"></i>
            </button>
          </div>
        </div>

        {/* Session Length Control */}
        <div className="length-control">
          <div id="session-label">Session Length</div>
          <div className="control-buttons">
            <button 
              id="session-decrement" 
              className="btn-level" 
              onClick={decrementSession}
            >
              <i className="fa fa-arrow-down fa-2x"></i>
            </button>
            <div id="session-length" className="btn-level">
              {sessionLength}
            </div>
            <button 
              id="session-increment" 
              className="btn-level" 
              onClick={incrementSession}
            >
              <i className="fa fa-arrow-up fa-2x"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Timer Display */}
      <div className="timer-container">
        <div className="timer-wrapper">
          <div id="timer-label" className="timer-label">
            {timerType}
          </div>
          <div id="time-left" className="time-left">
            {formatTime(timeLeft)}
          </div>
        </div>
      </div>

      {/* Timer Controls */}
      <div className="timer-controls">
        <button 
          id="start_stop" 
          className="timer-btn"
          onClick={toggleTimer}
        >
          <i className={`fa fa-${isRunning ? 'pause' : 'play'}`}></i>
        </button>
        <button 
          id="reset" 
          className="timer-btn"
          onClick={reset}
        >
          <i className="fa fa-refresh"></i>
        </button>
      </div>

      {/* Audio Element */}
      <audio 
        id="beep" 
        ref={audioBeep}
        src="https://raw.githubusercontent.com/freeCodeCamp/cdn/master/build/testable-projects-fcc/audio/BeepSound.wav"
        preload="auto"
      />
    </div>
  );
}

export default App;